import { Button, Collapse, Empty, Popover, Select, Spin, Tag } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle, HelpCircle } from "react-feather";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

import fallbackImage from "../../assets/images/shipping/blank_images.png";
import copyToClipboard from "../../utils/copyToClipboard";
import { formatDateTimeWithoutSeconds } from "../../utils/dateFormatter";
import ImageWithSpinner from "./ImageWithSpinner";
import PackagesViewer from "./PackagesViewer";
import PackingStation from "./PackingStation";
import ShippingStepper from "./ShippingStepper";

export default function Details({
  details,
  wegeOrderId,
  detailsLoading,
  searchTerm,
  onRefresh,
  selectedProviders = {},
  setSelectedProviders = () => {},
  onPlaceOrder,
  onPrint,
  printLoading = {},
  onOpenGuide,
}) {
  const { Option } = Select;
  const { t } = useTranslation();
  const [failedImages, setFailedImages] = useState({});
  const [selectedUnassigned, setSelectedUnassigned] = useState([]);
  const [creatingShipping, setCreatingShipping] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState({});
  const [trackingData, setTrackingData] = useState({});
  const [, setPackingReady] = useState({});
  const [selectedLoosePackages, setSelectedLoosePackages] = useState([]);
  const [creatingShippingFromPackages, setCreatingShippingFromPackages] =
    useState(false);
  const [rateLoading, setRateLoading] = useState({});
  const [activeShippingOrderId, setActiveShippingOrderId] = useState(null);
  const [stepperStep, setStepperStep] = useState(null);

  const handlePackingComplete = (shippingOrderId, isReady) => {
    setPackingReady((prev) => ({ ...prev, [shippingOrderId]: isReady }));
  };
  const providerSelections = selectedProviders || {};

  const handleProviderSelect = (shippingOrderId, value) => {
    if (typeof setSelectedProviders === "function") {
      setSelectedProviders((prev) => ({
        ...(prev || {}),
        [shippingOrderId]: value,
      }));
    }
  };

  // Add custom styles for slider dots
  useEffect(() => {
    // Create a style element
    const style = document.createElement("style");
    // Add the custom styles
    style.textContent = `
      .custom-dots {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }
      .custom-dots li {
        margin: 0 4px;
      }
      .custom-dots li button:before {
        font-size: 8px;
        color: #ccc;
        opacity: 0.8;
        margin-top: -5px;
      }
      .custom-dots li.slick-active button:before {
        color:rgb(143, 179, 214);
        opacity: 1;
      }
    `;
    // Append the style to the document head
    document.head.appendChild(style);

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleMarkArrived = async (orderId) => {
    try {
      await axios.post(`/alibaba/api/v1/mark-arrived/${orderId}/`);
      toast.success(t("orderDetails.orderMarkedSuccess"));
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || t("orderDetails.orderMarkedFailed"),
      );
    }
  };

  // Handle image error by trying the next image in the array
  const handleImageError = (itemId, imageIndex, item) => {
    const imagesArray = item.images ? item.images.split(",") : [];

    // If this is the last image in the array or there are no additional images, use fallback
    if (!imagesArray.length || imageIndex >= imagesArray.length - 1) {
      setFailedImages((prev) => ({
        ...prev,
        [itemId]: -1, // -1 indicates to use fallback
      }));
      return;
    }

    // Try the next image in the array
    setFailedImages((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] || 0) + 1,
    }));
  };

  // Get the current image source for an item
  const getImageSource = (item) => {
    // Don't update state here as it causes too many re-renders
    if (failedImages[item.id] === -1) {
      return fallbackImage;
    }

    const imagesArray = item.images ? item.images.split(",") : [];
    const imageToUse =
      failedImages[item.id] !== undefined && failedImages[item.id] >= 0
        ? imagesArray[failedImages[item.id]]
        : item.image;

    return `${process.env.REACT_APP_PUBLIC_MEDIA_URL}store/${item.store}/${imageToUse}`;
  };

  const handleToggleUnassigned = (orderId, checked) => {
    setSelectedUnassigned((prev) => {
      if (checked) {
        return prev.includes(orderId) ? prev : [...prev, orderId];
      }
      return prev.filter((id) => id !== orderId);
    });
  };

  const handleSelectAllUnassigned = (checked, orders) => {
    if (checked) {
      setSelectedUnassigned(orders.map((order) => order.id));
    } else {
      setSelectedUnassigned([]);
    }
  };

  const handleCreateShippingOrder = async () => {
    if (!selectedUnassigned.length) {
      return;
    }

    try {
      setCreatingShipping(true);
      const res = await axios.post("/shipping/api/v1/shipping/create/", {
        alibaba_order_ids: selectedUnassigned,
      });
      toast.success(
        t("orderDetails.shippingCreated", {
          defaultValue: "Shipping order created successfully",
        }),
      );
      if (res.data?.shipping_order_id) {
        setActiveShippingOrderId(res.data.shipping_order_id);
      }
      setSelectedUnassigned([]);
      if (onRefresh) {
        onRefresh();
      }
      return res.data;
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          t("orderDetails.shippingCreateFailed", {
            defaultValue: "Failed to create shipping order",
          }),
      );
      return false;
    } finally {
      setCreatingShipping(false);
    }
  };

  const handleCreateShippingFromPackages = async () => {
    if (!selectedLoosePackages.length) return;
    setCreatingShippingFromPackages(true);
    try {
      const res = await axios.post(
        "/shipping/api/v1/shipping/order-shipping/from-packages/",
        {
          package_ids: selectedLoosePackages,
        },
      );
      toast.success(
        t("orderDetails.shippingCreatedFromPackages", {
          defaultValue: "Shipping order created from selected packages.",
        }),
      );
      if (res.data?.shipping_order_id) {
        setActiveShippingOrderId(res.data.shipping_order_id);
      }
      setSelectedLoosePackages([]);
      if (onRefresh) onRefresh();
      return res.data;
    } catch (err) {
      if (
        err.response?.status === 400 &&
        err.response?.data?.missing_package_ids
      ) {
        const missing = err.response.data.missing_package_ids;
        setSelectedLoosePackages((prev) =>
          Array.from(new Set([...prev, ...missing])),
        );
        toast.error(
          t("orderDetails.splitOrderGuard", {
            defaultValue:
              "To avoid splitting a 1688 order, missing required packages were automatically selected. Please confirm and retry.",
          }),
        );
      } else if (
        err.response?.data?.blocked_reason === "incomplete_packing" &&
        err.response?.data?.items?.length > 0
      ) {
        const unpackedItems = err.response.data.items;
        const unpackedSkus = unpackedItems
          .map(
            (i) =>
              `${i.remaining_quantity}x ${i.wege_sku || i.order_1688_id || `Item #${i.order_item_id}`}`,
          )
          .join(", ");
        toast.error(
          t("orderDetails.unpackedItemsError", {
            orderId: unpackedItems[0].alibaba_order_id,
            skus: unpackedSkus,
            defaultValue: `Cannot create shipment: Order #${unpackedItems[0].alibaba_order_id} has unpacked items (${unpackedSkus}). Please pack them into a box in Step 2 first.`,
          }),
          { duration: 7000 },
        );
      } else {
        toast.error(
          err.response?.data?.message ||
            t("orderDetails.shippingCreateFailed", {
              defaultValue: "Failed to create shipping order",
            }),
        );
      }
      return false;
    } finally {
      setCreatingShippingFromPackages(false);
    }
  };

  const parseAramexDate = (value) => {
    if (!value) return "";
    const match = /\/Date\((\d+)([+-]\d+)?\)\//.exec(value);
    if (match) {
      const timestamp = Number(match[1]);
      if (!Number.isNaN(timestamp)) {
        try {
          return formatDateTimeWithoutSeconds(timestamp);
        } catch (e) {
          return value;
        }
      }
    }
    return value;
  };

  const handleFetchAramexTracking = async (shippingOrderId) => {
    if (!shippingOrderId) {
      return;
    }
    setTrackingLoading((prev) => ({ ...prev, [shippingOrderId]: true }));
    try {
      const { data } = await axios.post(
        "/shipping/api/v1/shipping/aramex/tracking/",
        {
          shipping_order_id: shippingOrderId,
        },
      );
      setTrackingData((prev) => ({ ...prev, [shippingOrderId]: data }));
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          t("orderDetails.trackingFetchFailed", {
            defaultValue: "Failed to fetch tracking information",
          }),
      );
    } finally {
      setTrackingLoading((prev) => ({ ...prev, [shippingOrderId]: false }));
    }
  };

  const handleRecalculateRate = async (shippingOrderId) => {
    if (!shippingOrderId) return;
    setRateLoading((prev) => ({ ...prev, [shippingOrderId]: true }));
    try {
      const res = await axios.post(
        `/shipping/api/v1/shipping/order-shipping/${shippingOrderId}/calculate-rate/`,
      );
      if (res.data?.ok) {
        toast.success(
          t("orderDetails.rateRecalculated", {
            cost: res.data.estimated_cost,
            currency: res.data.estimated_cost_currency || "AED",
            defaultValue: `Rate updated: ${res.data.estimated_cost} ${res.data.estimated_cost_currency || "AED"}`,
          }),
        );
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      const errData = err.response?.data;
      const errorMsg =
        errData?.message ||
        (errData?.error && typeof errData.error === "string"
          ? errData.error
          : null) ||
        (errData?.notifications
          ? JSON.stringify(errData.notifications)
          : null) ||
        t("orderDetails.rateRecalculateFailed", {
          defaultValue: "Failed to recalculate rate",
        });
      toast.error(errorMsg);
    } finally {
      setRateLoading((prev) => ({ ...prev, [shippingOrderId]: false }));
    }
  };

  const renderOrderItems = (items = []) => {
    if (!items.length) {
      return (
        <p style={{ padding: "8px", textAlign: "center" }}>
          {t("orderDetails.noItems")}
        </p>
      );
    }

    return (
      <div
        className="slider-container"
        style={{ padding: "10px", position: "relative" }}
      >
        <Slider
          dots
          infinite={false}
          speed={500}
          slidesToShow={1}
          slidesToScroll={1}
          dotsClass="slick-dots custom-dots"
          appendDots={(dots) => (
            <div
              style={{
                position: "absolute",
                bottom: "-15px",
                display: "block",
                width: "100%",
              }}
            >
              <ul style={{ margin: 0, padding: 0 }}>{dots}</ul>
            </div>
          )}
        >
          {items.map((item) => (
            <div key={item.id} className="slider-item">
              <div
                className="order-detail-item"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "3px",
                  backgroundColor: "#fafafa",
                  borderRadius: "6px",
                }}
              >
                <div
                  className="image-container"
                  style={{
                    width: "38%",
                    minWidth: "100px",
                    maxWidth: "180px",
                    flexShrink: 0,
                    position: "relative",
                  }}
                >
                  <ImageWithSpinner
                    item={item}
                    getImageSource={getImageSource}
                    handleImageError={handleImageError}
                    failedImages={failedImages}
                  />
                </div>
                <div
                  className="product-details"
                  style={{ width: "62%", flexGrow: 1 }}
                >
                  <p
                    style={{
                      fontSize: "14px",
                      marginBottom: "4px",
                    }}
                  >
                    <strong>{t("orderDetails.productName")}:</strong>{" "}
                    <Popover
                      content={item.product_name}
                      title={t("orderDetails.productName")}
                    >
                      <Link
                        to={`${item.external_link}`}
                        target="_blank"
                        style={{
                          color: "#1890ff",
                          textDecoration: "none",
                          borderBottom:
                            item.product_name?.length > 20
                              ? "1px dashed #999"
                              : "none",
                        }}
                      >
                        {item.product_name?.length > 20
                          ? `${item.product_name.substring(0, 15)}...`
                          : item.product_name}
                      </Link>
                    </Popover>
                  </p>
                  <p
                    style={{
                      fontSize: "12px",
                      marginBottom: "3px",
                    }}
                  >
                    <strong>{t("orderDetails.category")}:</strong>{" "}
                    {Array.isArray(item.category) ? (
                      <Popover
                        content={item.category.join(", ")}
                        title={t("orderDetails.category")}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            borderBottom: "1px dashed #999",
                          }}
                        >
                          {item.category.length > 2
                            ? `${item.category[0]}, ${item.category[1]}...`
                            : item.category.join(", ")}
                        </span>
                      </Popover>
                    ) : (
                      item.category || " "
                    )}
                  </p>
                  <p
                    style={{
                      fontSize: "13px",
                      marginBottom: "6px",
                    }}
                  >
                    <strong>{t("orderDetails.store")}:</strong> {item.store}
                  </p>
                  <p
                    style={{
                      fontSize: "12px",
                      marginBottom: "3px",
                    }}
                  >
                    <strong>{t("orderDetails.color")}:</strong>{" "}
                    {item.color ? (
                      <Popover
                        content={
                          typeof item.color === "object"
                            ? item.color?.name
                            : item.color
                        }
                        title={t("orderDetails.color")}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            borderBottom: "1px dashed #999",
                          }}
                        >
                          {typeof item.color === "object"
                            ? item.color?.name
                            : item.color}
                        </span>
                      </Popover>
                    ) : (
                      " "
                    )}
                  </p>
                  <p
                    style={{
                      fontSize: "13px",
                      marginBottom: "6px",
                    }}
                  >
                    <strong>{t("orderDetails.size")}:</strong>{" "}
                    {item.size ? (
                      <Popover
                        content={
                          typeof item.size === "object"
                            ? item.size?.name
                            : item.size
                        }
                        title={t("orderDetails.size")}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            borderBottom: "1px dashed #999",
                          }}
                        >
                          {typeof item.size === "object"
                            ? item.size?.name
                            : item.size}
                        </span>
                      </Popover>
                    ) : (
                      " "
                    )}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    );
  };

  const renderAlibabaOrder = (order) => (
    <div
      key={order.id}
      className="alibaba-order"
      style={{
        backgroundColor: order.status === "canceled" ? "#FFEFEF" : "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        marginBottom: "12px",
        overflow: "hidden",
      }}
    >
      <div
        className="alibaba-order-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #eee",
        }}
      >
        <div>
          <h4
            style={{
              marginBottom: 4,
              cursor: "pointer",
              userSelect: "none",
            }}
            onDoubleClick={() =>
              order.id &&
              copyToClipboard(String(order.id), {
                successMessage: t("copy.orderIdCopied", {
                  defaultValue: "Order ID copied",
                }),
              })
            }
            title={t("copy.doubleClickToCopy", {
              defaultValue: "Double-click to copy",
            })}
          >
            {t("orderDetails.alibabaOrder", {
              id: order.id,
              defaultValue: `1688 Order ${order.id}`,
            })}
          </h4>
          <p style={{ marginBottom: 4 }}>
            <strong>{t("orderDetails.trackingNumber")}:</strong>{" "}
            {searchTerm && order.tracking_number === searchTerm ? (
              <span
                style={{
                  backgroundColor: "#fffbe6",
                  padding: "0 4px",
                  cursor: order.tracking_number ? "pointer" : "default",
                  userSelect: "none",
                }}
                onDoubleClick={() =>
                  order.tracking_number &&
                  copyToClipboard(order.tracking_number, {
                    successMessage: t("copy.trackingNumberCopied", {
                      defaultValue: "Tracking number copied",
                    }),
                  })
                }
                title={
                  order.tracking_number
                    ? t("copy.doubleClickToCopy", {
                        defaultValue: "Double-click to copy",
                      })
                    : ""
                }
              >
                {order.tracking_number || t("table.common.na")}
              </span>
            ) : (
              <span
                style={{
                  cursor: order.tracking_number ? "pointer" : "default",
                  userSelect: "none",
                }}
                onDoubleClick={() =>
                  order.tracking_number &&
                  copyToClipboard(order.tracking_number, {
                    successMessage: t("copy.trackingNumberCopied", {
                      defaultValue: "Tracking number copied",
                    }),
                  })
                }
                title={
                  order.tracking_number
                    ? t("copy.doubleClickToCopy", {
                        defaultValue: "Double-click to copy",
                      })
                    : ""
                }
              >
                {order.tracking_number || t("table.common.na")}
              </span>
            )}
          </p>
          <p style={{ marginBottom: 4 }}>
            <strong>{t("orderDetails.status")}:</strong>{" "}
            {order.status || t("table.common.na")}
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>
              {t("orderDetails.logisticCode", {
                defaultValue: "Logistic Code",
              })}
              :
            </strong>{" "}
            <span
              style={{
                cursor: order.logistics_code ? "pointer" : "default",
                userSelect: "none",
              }}
              onDoubleClick={() =>
                order.logistics_code &&
                copyToClipboard(order.logistics_code, {
                  successMessage: t("copy.logisticCodeCopied", {
                    defaultValue: "Logistic code copied",
                  }),
                })
              }
              title={
                order.logistics_code
                  ? t("copy.doubleClickToCopy", {
                      defaultValue: "Double-click to copy",
                    })
                  : ""
              }
            >
              {order.logistics_code || t("table.common.na")}
            </span>
          </p>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          <Button
            type="primary"
            size="small"
            onClick={() => handleMarkArrived(order.id)}
            disabled={order.status === "Arrived" || order.status === "canceled"}
          >
            <CheckCircle size={14} style={{ marginRight: 6 }} />
            {t("orderDetails.markArrived")}
          </Button>
        </div>
      </div>
      {renderOrderItems(order.order_items)}
    </div>
  );

  const groups = details?.groups || [];
  const unassignedAlibabaOrders =
    details?.unassigned_alibaba_orders || details?.unassigned || [];
  const loosePackages =
    details?.loose_packages ||
    details?.unassigned_packages ||
    details?.packages ||
    [];
  const currentWegeOrderId =
    wegeOrderId || details?.id || details?.wege_order_id || details?.order_id;

  const activeShippingOrderGroup =
    groups.find((g) => g.shipping_order?.id === activeShippingOrderId) ||
    (groups.length > 0 ? groups[groups.length - 1] : null);


  // Auto-initialize provider selections to "aramex" for unplaced orders
  useEffect(() => {
    if (groups.length && typeof setSelectedProviders === "function") {
      groups.forEach((group) => {
        const shippingOrder = group.shipping_order || {};
        const isOrderUnplaced =
          (!shippingOrder.tracking_number ||
            shippingOrder.tracking_number === "") &&
          (!shippingOrder.provider || shippingOrder.provider === "");

        if (
          isOrderUnplaced &&
          shippingOrder.id &&
          !providerSelections[shippingOrder.id]
        ) {
          setSelectedProviders((prev) => ({
            ...(prev || {}),
            [shippingOrder.id]: "aramex",
          }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length, details]);

  const renderGroups = () => {
    if (!groups.length) {
      return null;
    }

    return (
      <div>
        <h3 style={{ marginBottom: "12px" }}>
          {t("orderDetails.shippingOrders", {
            defaultValue: "Shipping Orders",
          })}
        </h3>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {groups.map((group) => {
            const shippingOrder =
              group.shipping_order ||
              (Array.isArray(group.shipping_orders)
                ? group.shipping_orders[0]
                : null) ||
              {};
            const attention =
              shippingOrder?.attention ||
              (Array.isArray(group?.shipping_orders)
                ? group.shipping_orders.find(
                    (s) => s?.attention?.needs_attention,
                  )?.attention
                : null) ||
              group?.attention;
            const hasAttention = !!attention?.needs_attention;
            const tracking = trackingData[shippingOrder.id];
            const isOrderUnplaced =
              (!shippingOrder.tracking_number ||
                shippingOrder.tracking_number === "") &&
              (!shippingOrder.provider || shippingOrder.provider === "");

            const allCanceled =
              group.alibaba_orders?.length > 0 &&
              group.alibaba_orders.every(
                (o) => o.status === "canceled" || o.status === "cancelled",
              );

            return (
              <Collapse
                key={shippingOrder.id || `group-${Math.random()}`}
                defaultActiveKey={allCanceled ? [] : ["1"]}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: hasAttention
                    ? "1px solid #ffccc7"
                    : "1px solid #e5e5e5",
                }}
              >
                <Collapse.Panel
                  key="1"
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>
                        {t("orderDetails.shippingOrderHeader", {
                          defaultValue: "Shipping Order",
                        })}{" "}
                        #{shippingOrder.id || "N/A"}
                      </span>
                      {hasAttention && (
                        <Tag
                          color="error"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            fontWeight: 600,
                            padding: "0 6px",
                            borderRadius: "4px",
                          }}
                        >
                          <AlertTriangle size={12} />
                          {t("shipping.needsAttention", "Needs Attention")}
                        </Tag>
                      )}
                    </div>
                  }
                >
                  {hasAttention && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        padding: "10px 14px",
                        backgroundColor: "#fff2f0",
                        border: "1px solid #ffccc7",
                        borderRadius: "8px",
                        marginBottom: "16px",
                        color: "#cf1322",
                      }}
                    >
                      <AlertTriangle
                        size={18}
                        style={{
                          flexShrink: 0,
                          marginTop: "2px",
                          color: "#cf1322",
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "13px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <span>
                            {t("shipping.needsAttention", "Needs Attention")}
                          </span>
                          {attention.at && (
                            <span
                              style={{
                                fontWeight: "normal",
                                fontSize: "12px",
                                color: "#8c8c8c",
                              }}
                            >
                              ({formatDateTimeWithoutSeconds(attention.at)})
                            </span>
                          )}
                        </div>
                        {attention.note && (
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#434343",
                              marginTop: "4px",
                              lineHeight: "1.4",
                            }}
                          >
                            {attention.note}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "12px 18px",
                        alignItems: "center",
                      }}
                    >
                      <span>
                        <strong>
                          {t("orderDetails.shippingOrderId", {
                            defaultValue: "Shipping Order ID",
                          })}
                          :
                        </strong>{" "}
                        <span
                          style={{
                            cursor: shippingOrder.id ? "pointer" : "default",
                            userSelect: "none",
                          }}
                          onDoubleClick={() =>
                            shippingOrder.id &&
                            copyToClipboard(String(shippingOrder.id), {
                              successMessage: t("copy.shippingOrderIdCopied", {
                                defaultValue: "Shipping Order ID copied",
                              }),
                            })
                          }
                          title={
                            shippingOrder.id
                              ? t("copy.doubleClickToCopy", {
                                  defaultValue: "Double-click to copy",
                                })
                              : ""
                          }
                        >
                          {shippingOrder.id}
                        </span>
                      </span>
                      <span>
                        <strong>
                          {t("orderDetails.provider", {
                            defaultValue: "Provider",
                          })}
                          :
                        </strong>{" "}
                        {shippingOrder.provider || t("table.common.na")}
                      </span>
                      <span>
                        <strong>{t("orderDetails.trackingNumber")}:</strong>{" "}
                        <span
                          style={{
                            cursor: shippingOrder.tracking_number
                              ? "pointer"
                              : "default",
                            userSelect: "none",
                          }}
                          onDoubleClick={() =>
                            shippingOrder.tracking_number &&
                            copyToClipboard(shippingOrder.tracking_number, {
                              successMessage: t("copy.trackingNumberCopied", {
                                defaultValue: "Tracking number copied",
                              }),
                            })
                          }
                          title={
                            shippingOrder.tracking_number
                              ? t("copy.doubleClickToCopy", {
                                  defaultValue: "Double-click to copy",
                                })
                              : ""
                          }
                        >
                          {shippingOrder.tracking_number ||
                            t("table.common.na")}
                        </span>
                      </span>
                      {group.packing_summary && (
                        <span style={{ marginLeft: "16px" }}>
                          <strong>{t("packing.summary", "Packing:")}</strong>{" "}
                          {group.packing_summary.ready_for_submission ? (
                            <span style={{ color: "green" }}>
                              {t("packing.ready", "Ready")}
                            </span>
                          ) : (
                            <span style={{ color: "#d97706" }}>
                              {group.packing_summary.items_fully_packed} /{" "}
                              {group.packing_summary.items_total}{" "}
                              {t("packing.itemsPacked", "Items Packed")}(
                              {group.packing_summary.packages_count}{" "}
                              {t("packing.boxes", "Boxes")})
                            </span>
                          )}
                        </span>
                      )}
                      {group.estimated_shipping_cost &&
                        group.estimated_shipping_cost.amount !== null &&
                        group.estimated_shipping_cost.amount !== undefined && (
                          <span style={{ marginLeft: "16px" }}>
                            <strong>
                              {t("packing.estCost", {
                                defaultValue: "Est. Cost:",
                              })}
                            </strong>{" "}
                            <span style={{ color: "#0284c7", fontWeight: 600 }}>
                              {!isNaN(
                                Number(group.estimated_shipping_cost.amount),
                              )
                                ? Number(
                                    group.estimated_shipping_cost.amount,
                                  ).toFixed(2)
                                : group.estimated_shipping_cost.amount}{" "}
                              {group.estimated_shipping_cost.currency || "AED"}
                            </span>
                          </span>
                        )}
                      <span style={{ marginLeft: "16px" }}>
                        <strong>{t("orderDetails.status")}:</strong>{" "}
                        {(() => {
                          const statusStr = shippingOrder.status;
                          if (!statusStr)
                            return <Tag>{t("table.common.na")}</Tag>;
                          const lower = statusStr.toLowerCase();
                          let tagColor = "default";
                          if (lower === "arrived") tagColor = "success";
                          else if (lower === "returned") tagColor = "warning";
                          else if (
                            lower === "lostconfiscated" ||
                            lower === "lost_confiscated"
                          )
                            tagColor = "error";
                          else if (
                            lower === "tracking" ||
                            lower === "shipping"
                          )
                            tagColor = "processing";
                          return (
                            <Tag color={tagColor}>
                              {t(`order.status.${lower}`, {
                                defaultValue: statusStr,
                              })}
                            </Tag>
                          );
                        })()}
                      </span>
                      <span>
                        <strong>{t("orderDetails.createdAt")}:</strong>{" "}
                        {shippingOrder.created_at
                          ? formatDateTimeWithoutSeconds(
                              shippingOrder.created_at,
                            )
                          : t("table.common.na")}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      {(() => {
                        // Auto-select "aramex" if no provider is set
                        const providerValue =
                          providerSelections[shippingOrder.id] ||
                          (shippingOrder.provider &&
                          shippingOrder.provider !== ""
                            ? shippingOrder.provider
                            : "aramex");

                        if (isOrderUnplaced) {
                          return group.packing_summary?.ready_for_submission ? (
                            <>
                              <Select
                                size="small"
                                style={{ minWidth: 150 }}
                                value={providerValue}
                                onChange={(value) =>
                                  handleProviderSelect(shippingOrder.id, value)
                                }
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Option value="aramex">Aramex</Option>
                                <Option value="vnlin">Vnlin</Option>
                                <Option value="imile">Imile</Option>
                              </Select>
                              <Button
                                type="primary"
                                size="small"
                                onClick={() =>
                                  onPlaceOrder?.(
                                    shippingOrder.id,
                                    providerValue,
                                  )
                                }
                              >
                                {t("placeOrder.title")}
                              </Button>
                            </>
                          ) : null;
                        }
                        return (
                          <>
                            <Button
                              type="primary"
                              size="small"
                              onClick={() => onPrint?.(shippingOrder.id)}
                              loading={!!printLoading[shippingOrder.id]}
                              disabled={!shippingOrder.tracking_number}
                            >
                              {t("placeOrder.print")}
                            </Button>
                            {shippingOrder.provider?.toLowerCase() ===
                              "aramex" && (
                              <>
                                <Button
                                  type="default"
                                  size="small"
                                  onClick={() =>
                                    handleFetchAramexTracking(shippingOrder.id)
                                  }
                                  loading={!!trackingLoading[shippingOrder.id]}
                                >
                                  {t("orderDetails.fetchAramexTracking", {
                                    defaultValue: "Fetch Aramex Tracking",
                                  })}
                                </Button>
                                <Button
                                  type="default"
                                  size="small"
                                  onClick={() =>
                                    handleRecalculateRate(shippingOrder.id)
                                  }
                                  loading={!!rateLoading[shippingOrder.id]}
                                >
                                  {t("orderDetails.recalculateRate", {
                                    defaultValue: "Recalculate Rate",
                                  })}
                                </Button>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {isOrderUnplaced && (
                    <div style={{ marginTop: "12px" }}>
                      <PackingStation
                        shippingOrderId={shippingOrder.id}
                        onPackingComplete={(ready) => {
                          handlePackingComplete(shippingOrder.id, ready);
                        }}
                        onRefresh={onRefresh}
                      />
                    </div>
                  )}

                  <PackagesViewer
                    shippingOrderId={shippingOrder.id}
                    refreshKey={group.packing_summary?.packages_count}
                  />

                  {!!tracking?.events?.length && (
                    <div style={{ marginTop: "16px" }}>
                      <h4 style={{ marginBottom: "8px" }}>
                        {t("orderDetails.aramexEvents", {
                          defaultValue: "Aramex Events",
                        })}
                      </h4>
                      <div style={{ overflowX: "auto" }}>
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>
                                {t("orderDetails.eventCode", {
                                  defaultValue: "Code",
                                })}
                              </th>
                              <th>
                                {t("orderDetails.eventDescription", {
                                  defaultValue: "Description",
                                })}
                              </th>
                              <th>
                                {t("orderDetails.eventDate", {
                                  defaultValue: "Date & Time",
                                })}
                              </th>
                              <th>
                                {t("orderDetails.location", {
                                  defaultValue: "Location",
                                })}
                              </th>
                              <th>
                                {t("orderDetails.comments", {
                                  defaultValue: "Comments",
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {tracking.events.map((event, idx) => (
                              <tr key={`${event.code}-${idx}`}>
                                <td
                                  style={{
                                    cursor: event.code ? "pointer" : "default",
                                    userSelect: "none",
                                  }}
                                  onDoubleClick={() =>
                                    event.code &&
                                    copyToClipboard(event.code, {
                                      successMessage: t(
                                        "copy.eventCodeCopied",
                                        {
                                          defaultValue: "Event code copied",
                                        },
                                      ),
                                    })
                                  }
                                  title={
                                    event.code
                                      ? t("copy.doubleClickToCopy", {
                                          defaultValue: "Double-click to copy",
                                        })
                                      : ""
                                  }
                                >
                                  {event.code}
                                </td>
                                <td
                                  style={{
                                    cursor: event.description
                                      ? "pointer"
                                      : "default",
                                    userSelect: "none",
                                  }}
                                  onDoubleClick={() =>
                                    event.description &&
                                    copyToClipboard(event.description, {
                                      successMessage: t(
                                        "copy.descriptionCopied",
                                        {
                                          defaultValue: "Description copied",
                                        },
                                      ),
                                    })
                                  }
                                  title={
                                    event.description
                                      ? t("copy.doubleClickToCopy", {
                                          defaultValue: "Double-click to copy",
                                        })
                                      : ""
                                  }
                                >
                                  {event.description}
                                </td>
                                <td>{parseAramexDate(event.datetime)}</td>
                                <td
                                  style={{
                                    cursor: event.location
                                      ? "pointer"
                                      : "default",
                                    userSelect: "none",
                                  }}
                                  onDoubleClick={() =>
                                    event.location &&
                                    copyToClipboard(event.location, {
                                      successMessage: t("copy.locationCopied", {
                                        defaultValue: "Location copied",
                                      }),
                                    })
                                  }
                                  title={
                                    event.location
                                      ? t("copy.doubleClickToCopy", {
                                          defaultValue: "Double-click to copy",
                                        })
                                      : ""
                                  }
                                >
                                  {event.location}
                                </td>
                                <td
                                  style={{
                                    cursor: event.comments
                                      ? "pointer"
                                      : "default",
                                    userSelect: "none",
                                  }}
                                  onDoubleClick={() =>
                                    event.comments &&
                                    copyToClipboard(event.comments, {
                                      successMessage: t("copy.commentsCopied", {
                                        defaultValue: "Comments copied",
                                      }),
                                    })
                                  }
                                  title={
                                    event.comments
                                      ? t("copy.doubleClickToCopy", {
                                          defaultValue: "Double-click to copy",
                                        })
                                      : ""
                                  }
                                >
                                  {event.comments}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: "16px",
                      display: "grid",
                      gap: "16px",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(320px, 1fr))",
                    }}
                  >
                    {group.alibaba_orders?.map((order) =>
                      renderAlibabaOrder(order),
                    )}
                  </div>
                </Collapse.Panel>
              </Collapse>
            );
          })}
        </div>
      </div>
    );
  };


  if (detailsLoading && !details) {
    return (
      <div style={{ padding: "16px", textAlign: "center" }}>
        <Spin />
      </div>
    );
  }

  if (!details) {
    return (
      <div style={{ padding: "16px" }}>
        <Empty description={t("orderDetails.noOrderDetails")} />
      </div>
    );
  }

  const hasUnassignedOrLoose =
    unassignedAlibabaOrders.length > 0 || loosePackages.length > 0;

  return (
    <div className="expanded-row">
      {onOpenGuide && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "12px",
          }}
        >
          <Button
            size="small"
            type="link"
            icon={<HelpCircle size={14} style={{ marginRight: 4 }} />}
            onClick={onOpenGuide}
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "#1890ff",
              padding: 0,
            }}
          >
            {t(
              "guide.howItWorks",
              "How does packaging & shipping work? View Guide",
            )}
          </Button>
        </div>
      )}

      {hasUnassignedOrLoose ? (
        <>
          <div style={{ marginBottom: "20px" }}>
            <ShippingStepper
              unassignedAlibabaOrders={unassignedAlibabaOrders}
              selectedUnassigned={selectedUnassigned}
              setSelectedUnassigned={setSelectedUnassigned}
              handleToggleUnassigned={handleToggleUnassigned}
              handleSelectAllUnassigned={handleSelectAllUnassigned}
              handleMarkArrived={handleMarkArrived}
              handleCreateShippingOrder={handleCreateShippingOrder}
              creatingShipping={creatingShipping}
              renderOrderItems={renderOrderItems}
              searchTerm={searchTerm}
              wegeOrderId={currentWegeOrderId}
              loosePackages={loosePackages}
              selectedLoosePackages={selectedLoosePackages}
              setSelectedLoosePackages={setSelectedLoosePackages}
              handleCreateShippingFromPackages={
                handleCreateShippingFromPackages
              }
              creatingShippingFromPackages={creatingShippingFromPackages}
              groups={groups}
              activeShippingOrderGroup={activeShippingOrderGroup}
              setActiveShippingOrderGroup={(group) => {
                setActiveShippingOrderId(group?.shipping_order?.id || null);
              }}
              providerSelections={providerSelections}
              handleProviderSelect={handleProviderSelect}
              onPlaceOrder={onPlaceOrder}
              onPrint={onPrint}
              printLoading={printLoading}
              handleRecalculateRate={handleRecalculateRate}
              rateLoading={rateLoading}
              handleFetchAramexTracking={handleFetchAramexTracking}
              trackingLoading={trackingLoading}
              trackingData={trackingData}
              onRefresh={onRefresh}
              currentStepProp={stepperStep}
              setCurrentStepProp={setStepperStep}
              t={t}
            />
          </div>

          {groups.length > 0 && (
            <div style={{ marginTop: "24px" }}>
              <Collapse
                ghost
                items={[
                  {
                    key: "existing-shipments",
                    label: (
                      <span style={{ fontWeight: 600, color: "#475569" }}>
                        {t(
                          "orderDetails.existingDispatchedShipments",
                          "Existing Dispatched Shipments",
                        )}{" "}
                        ({groups.length})
                      </span>
                    ),
                    children: renderGroups(),
                  },
                ]}
              />
            </div>
          )}
        </>
      ) : groups.length > 0 ? (
        <div>{renderGroups()}</div>
      ) : (
        <Empty description={t("orderDetails.noOrderDetails")} />
      )}
    </div>
  );
}
